import React, { useEffect, useRef } from 'react';
import CollapsibleSection from './CollapsibleSection';
import GroupedCardList from './GroupedCardList';
import { normalizeSlashKeys, countTriggers, countGroups } from './utils';
import { Plus, X } from 'lucide-react';

interface SpeechOverrides {
  hearing_corrections?: Record<string, string>;
  alias_map?: Record<string, string>;
  speaking_guide?: Record<string, string>;
  speech_rules?: string[];
}

interface SpeechOverridesPanelProps {
  speechOverrides: SpeechOverrides | undefined;
  onUpdateOverride: (section: keyof SpeechOverrides, value: any) => void;
  menuItems: Array<{ item_name: string; description: string; section: string; cuisine_type: string; price: number }>;
  onShowToast?: (message: string) => void;
}

/**
 * Reusable key-value adder for flat sections (pronunciation guide).
 */
const KeyValueAdder: React.FC<{
  keyPlaceholder: string;
  valuePlaceholder: string;
  onAdd: (key: string, value: string) => void;
}> = ({ keyPlaceholder, valuePlaceholder, onAdd }) => {
  const [newKey, setNewKey] = React.useState('');
  const [newValue, setNewValue] = React.useState('');
  return (
    <div className="flex items-center gap-2 mt-1">
      <input
        type="text"
        value={newKey}
        onChange={(e) => setNewKey(e.target.value)}
        placeholder={keyPlaceholder}
        className="text-sm px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 flex-1"
      />
      <input
        type="text"
        value={newValue}
        onChange={(e) => setNewValue(e.target.value)}
        placeholder={valuePlaceholder}
        className="text-sm px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 flex-1"
        onKeyDown={(e) => { if (e.key === 'Enter' && newKey && newValue) { onAdd(newKey, newValue); setNewKey(''); setNewValue(''); } }}
      />
      <button
        onClick={() => { if (newKey && newValue) { onAdd(newKey, newValue); setNewKey(''); setNewValue(''); } }}
        disabled={!newKey.trim() || !newValue.trim()}
        className="p-1.5 rounded-lg bg-purple-100 text-purple-600 hover:bg-purple-200 disabled:opacity-30 disabled:cursor-not-allowed"
      >
        <Plus className="w-4 h-4" />
      </button>
    </div>
  );
};

/**
 * Reusable rule adder for speech rules.
 */
const RuleAdder: React.FC<{ onAdd: (rule: string) => void }> = ({ onAdd }) => {
  const [newRule, setNewRule] = React.useState('');
  return (
    <div className="flex items-center gap-2 mt-1">
      <input
        type="text"
        value={newRule}
        onChange={(e) => setNewRule(e.target.value)}
        placeholder='e.g., Always say "filet" not "fillet"'
        className="text-sm px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 flex-1"
        onKeyDown={(e) => { if (e.key === 'Enter' && newRule) { onAdd(newRule); setNewRule(''); } }}
      />
      <button
        onClick={() => { if (newRule) { onAdd(newRule); setNewRule(''); } }}
        disabled={!newRule.trim()}
        className="p-1.5 rounded-lg bg-purple-100 text-purple-600 hover:bg-purple-200 disabled:opacity-30 disabled:cursor-not-allowed"
      >
        <Plus className="w-4 h-4" />
      </button>
    </div>
  );
};

const SpeechOverridesPanel: React.FC<SpeechOverridesPanelProps> = ({
  speechOverrides,
  onUpdateOverride,
  menuItems,
  onShowToast,
}) => {
  const migrationDone = useRef(false);

  // "/" migration: split compound keys on first load
  useEffect(() => {
    if (migrationDone.current || !speechOverrides) return;
    migrationDone.current = true;

    let migrated = false;
    const sections: (keyof Pick<SpeechOverrides, 'hearing_corrections' | 'alias_map' | 'speaking_guide'>)[] = [
      'hearing_corrections', 'alias_map', 'speaking_guide'
    ];

    for (const section of sections) {
      const data = speechOverrides[section];
      if (data && Object.keys(data).length > 0) {
        const [normalized, wasModified] = normalizeSlashKeys(data);
        if (wasModified) {
          onUpdateOverride(section, normalized);
          migrated = true;
        }
      }
    }

    if (migrated && onShowToast) {
      onShowToast('Expanded grouped entries into individual triggers. Save to apply.');
    }
  }, [speechOverrides, onUpdateOverride, onShowToast]);

  const hearingData = speechOverrides?.hearing_corrections || {};
  const aliasData = speechOverrides?.alias_map || {};
  const speakingData = speechOverrides?.speaking_guide || {};
  const rulesData = speechOverrides?.speech_rules || [];

  const addKeyValue = (section: 'hearing_corrections' | 'alias_map' | 'speaking_guide', key: string, value: string) => {
    if (!key.trim() || !value.trim()) return;
    const current = speechOverrides?.[section] || {};
    onUpdateOverride(section, { ...current, [key.trim().toLowerCase()]: value.trim() });
  };

  const removeKey = (section: 'hearing_corrections' | 'alias_map' | 'speaking_guide', key: string) => {
    const current = { ...(speechOverrides?.[section] || {}) };
    delete current[key];
    onUpdateOverride(section, current);
  };

  const addRule = (rule: string) => {
    if (!rule.trim()) return;
    const current = speechOverrides?.speech_rules || [];
    onUpdateOverride('speech_rules', [...current, rule.trim()]);
  };

  const removeRule = (index: number) => {
    const current = [...(speechOverrides?.speech_rules || [])];
    current.splice(index, 1);
    onUpdateOverride('speech_rules', current);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
      <h4 className="text-sm font-semibold text-gray-900 mb-1">Restaurant Overrides</h4>
      <p className="text-xs text-gray-500 mb-4">
        Add corrections specific to your restaurant. Use the AI generator above or add manually.
      </p>

      {/* Hearing Corrections — grouped cards */}
      <CollapsibleSection
        label="Hearing Corrections"
        triggerCount={countTriggers(hearingData)}
        groupCount={countGroups(hearingData)}
        badgeColor="bg-blue-100 text-blue-700"
      >
        <p className="text-xs text-gray-400 mb-2">Customer says &rarr; What they actually mean</p>
        <GroupedCardList
          sectionType="hearing_corrections"
          data={hearingData}
          onAdd={(k, v) => addKeyValue('hearing_corrections', k, v)}
          onRemove={(k) => removeKey('hearing_corrections', k)}
        />
      </CollapsibleSection>

      {/* Item Aliases — grouped cards */}
      <CollapsibleSection
        label="Item Aliases"
        triggerCount={countTriggers(aliasData)}
        groupCount={countGroups(aliasData)}
        badgeColor="bg-green-100 text-green-700"
      >
        <p className="text-xs text-gray-400 mb-2">Customer phrase &rarr; Actual menu item</p>
        <GroupedCardList
          sectionType="alias_map"
          data={aliasData}
          onAdd={(k, v) => addKeyValue('alias_map', k, v)}
          onRemove={(k) => removeKey('alias_map', k)}
          menuItems={menuItems}
        />
      </CollapsibleSection>

      {/* Pronunciation Guide — flat rows (no grouping needed) */}
      <CollapsibleSection
        label="Pronunciation Guide"
        triggerCount={Object.keys(speakingData).length}
        badgeColor="bg-purple-100 text-purple-700"
      >
        <p className="text-xs text-gray-400 mb-2">Word &rarr; Say it as</p>
        {Object.entries(speakingData).map(([key, value]) => (
          <div key={key} className="flex items-center gap-2 mb-2">
            <span className="text-sm text-gray-600 bg-gray-50 px-3 py-1.5 rounded border min-w-[120px]">{key}</span>
            <span className="text-gray-400">&rarr;</span>
            <span className="text-sm text-gray-800 bg-purple-50 px-3 py-1.5 rounded border flex-1">"{value}"</span>
            <button onClick={() => removeKey('speaking_guide', key)} className="text-red-400 hover:text-red-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
        <KeyValueAdder
          keyPlaceholder="Word (e.g., acai)"
          valuePlaceholder="Say it as (e.g., ah-sah-EE)"
          onAdd={(k, v) => addKeyValue('speaking_guide', k, v)}
        />
      </CollapsibleSection>

      {/* Speech Rules — flat rows */}
      <CollapsibleSection
        label="Speech Rules"
        triggerCount={rulesData.length}
        badgeColor="bg-orange-100 text-orange-700"
      >
        <p className="text-xs text-gray-400 mb-2">What the AI should or should not say</p>
        {rulesData.map((rule, i) => (
          <div key={i} className="flex items-center gap-2 mb-2">
            <span className="text-sm text-gray-800 bg-orange-50 px-3 py-1.5 rounded border flex-1">{rule}</span>
            <button onClick={() => removeRule(i)} className="text-red-400 hover:text-red-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
        <RuleAdder onAdd={addRule} />
      </CollapsibleSection>
    </div>
  );
};

export default SpeechOverridesPanel;
